import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import { jwtDecode } from 'jwt-decode';

import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';
import {
    acoperire,
    adaugaEsantion,
    casuteVizionate,
    formateazaDurata,
    formateazaDurataLunga,
    gardaSaltInainte,
    poateContinua,
    secundeRamase,
    secundeVazute,
    type Segment
} from '../utils/video-coverage';
import { STARE, idVideoDinUrl, incarcaApiYouTube, type PlayerYouTube } from '../utils/youtube-player';
import { useMarcheazaVideoVazut } from '../hooks/useMarcheazaVideoVazut';
import CadruPasVideo from '../components/CadruPasVideo';
import { resolvePostAuthTarget } from '../utils/resolve-post-auth-target';
import { pasiInscriereActive } from '../utils/pasi-inscriere';
import type { UserInfo } from '../../../providers/auth-provider.types';
import { trackIntroVideoWatched } from '@/telemetry/posthog-events';

/**
 * Pasul de video din inscriere (U4), pe macheta `b-pas-video.html`.
 *
 * Fluxul clientului: profesorul reconfirma datele, vede un material scurt
 * CAP-COADA, si abia apoi merge mai departe. „Cap-coada" NU inseamna evenimentul
 * de final al playerului — ala se obtine tragand cursorul in doua secunde. Se
 * masoara segmentele parcurse (`utils/video-coverage.ts`).
 *
 * 🔴 Din 21.09 (item 2, decizia owner-ului): inapoi DA, inainte NU. Pana atunci derularea era
 * permisa si doar nu se numara; acum un salt dincolo de cel mai departe punct vazut
 * (`maxVazut`, plus 2 s toleranta) intoarce playerul acolo, cu un mesaj scurt, vizibil si anuntat
 * (`aria-live="polite"`). Regula e in `gardaSaltInainte`, pura si testata; aici doar se aplica.
 * `disablekb: 1` scoate sagetile si cifrele playerului (salturi de 5 s / 10 %), dar nu e garda —
 * bara de progres ramane, deci garda ramane cea de la esantionare.
 *
 * ⚠️ Bara de sub player arata SEGMENTELE vazute, nu pozitia cursorului. Golul din
 * mijloc spune singur regula — de-aia textul de sub buton se schimba in „portiunea
 * sarita nu se bifeaza", in loc sa fie o explicatie pusa undeva la inceput.
 *
 * Fara lacat pe player: videoul se poate deschide si derula oricum; blocat e
 * pasul urmator, deci starea apartine BUTONULUI.
 *
 * Din SP-03, starea NU mai traieste doar in memoria paginii: la atingerea pragului
 * se cheama `PATCH /api/school-requests/own/video`, o singura data per montare.
 * Marcajul e al backendului, dar ecranul nu depinde de el — daca apelul pica,
 * butonul ramane deschis (pragul e atins, si asta e adevarat local) si nimeni nu
 * primeste un mesaj despre ceva ce n-are cum sa repare.
 *
 * ⚠️ Pagina cere context de router: «Continua» se intoarce la pagina de asteptare
 * (de unde vine si legatura incoace), iar caseta de eroare are o iesire proprie.
 * Prima versiune n-avea nici una, nici alta: butonul se deschidea corect si nu
 * ducea nicaieri, pentru ca ruta il monta fara `onContinua`, iar `onContinua?.()`
 * pe `undefined` nu face si nu spune nimic.
 */

/** Pasul sondajului. Sub el nu se castiga precizie vizibila pe un ecran mic. */
const PAS_SONDAJ_MS = 250;
/** Casutele barei de segmente, ca in macheta. */
const NR_CASUTE = 12;
/**
 * Cat asteptam durata de la player inainte sa recunoastem ca nu vine.
 *
 * Fara pragul asta ecranul e un fund de sac: o durata necunoscuta da acoperire 0
 * (deliberat, `video-coverage.ts`), deci poarta ramane inchisa la infinit, fara
 * niciun mesaj. 12 s e generos pentru o retea mobila proasta si totusi marginit.
 */
const PRAG_DURATA_MS = 12_000;
/**
 * SE-12 (owner, 2026-09-16): revenire la poarta — «Continuă» se deschide DOAR la pragul de 95 %
 * (`ACOPERIRE_NECESARA`, `video-coverage.ts`), nu de la inceput. Era `true` temporar, din
 * 2026-09-15, cat timp filmul se putea sari.
 */
const VIDEO_INTRO_SARIBIL = false;
/** Cat ramane pe ecran mesajul garzii de salt. Destul cat sa fie citit, nu cat sa ramana lipit. */
const DURATA_AVERTISMENT_SALT_MS = 5_000;

interface Props {
    /**
     * URL-ul materialului, de la server.
     *
     * 🔴 OBLIGATORIU, si fara valoare implicita. Pana la b21 aici statea scris in
     * cod filmuletul de test dat de owner in august, ca default de prop — deci un
     * apelant care nu stia ce URL sa dea primea, tacut, ALT material decat cel al
     * clientului, pe un ecran care arata perfect sanatos. Sursa se decide acum in
     * `utils/video-sursa.ts`, care nu intoarce niciodata un URL pe care nu l-a dat
     * serverul; componenta asta doar il primeste.
     */
    urlVideo: string;
    /** Ce se intampla la «Continua». Injectabil; implicit, intoarcerea la pagina de asteptare. */
    onContinua?: () => void;
}

export default function VideoIntroPage({ urlVideo, onContinua }: Props) {
    const idVideo = React.useMemo(() => idVideoDinUrl(urlVideo), [urlVideo]);

    const navigate = useNavigate();
    const { marcheaza, asteaptaMarcaj } = useMarcheazaVideoVazut();
    const { PATHS, PAGES, ROLES, getUserHomePage } = useRoutesProvider();
    const { handleTokenRefresh } = useAuthProviderV2();
    const caleAsteptare: string = PATHS[PAGES.ACCOUNT_UNAUTHORIZED_TEACHER];

    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const playerRef = React.useRef<PlayerYouTube | null>(null);
    const pozitieAnterioara = React.useRef<number | null>(null);
    /* Garda de salt: cel mai departe punct atins legitim, si ce trebuie ca sa se poata spune cat
       a avut timp sa ruleze playerul intre doua esantioane (vezi `gardaSaltInainte`). Refuri, nu
       stare: se citesc si se scriu la fiecare 250 ms, fara sa aiba ce re-randa. */
    const maxVazut = React.useRef(0);
    const ruleaza = React.useRef(false);
    const esantionAnterior = React.useRef<{ la: number; ruleaza: boolean } | null>(null);
    const ceasAvertisment = React.useRef<number | null>(null);

    const [durata, setDurata] = React.useState(0);
    const [segmente, setSegmente] = React.useState<Segment[]>([]);
    const [pornit, setPornit] = React.useState(false);
    const [gata, setGata] = React.useState(false);
    const [eroare, setEroare] = React.useState<string | null>(null);
    const [avertismentSalt, setAvertismentSalt] = React.useState(false);

    React.useEffect(() => () => {
        if (ceasAvertisment.current !== null) window.clearTimeout(ceasAvertisment.current);
    }, []);

    React.useEffect(() => {
        if (!idVideo) {
            setEroare('Materialul video nu a putut fi identificat.');
            return;
        }
        let anulat = false;

        incarcaApiYouTube().then(() => {
            if (anulat || !containerRef.current) return;
            const YT = (window as unknown as { YT?: any }).YT;
            if (!YT?.Player) {
                setEroare('Materialul video nu a putut fi încărcat.');
                return;
            }
            playerRef.current = new YT.Player(containerRef.current, {
                videoId: idVideo,
                // SE-12: API-ul YouTube INLOCUIESTE div-ul cu un `<iframe>` care nu
                // mosteneste clasele Tailwind ale lui (`absolute inset-0 h-full w-full`) —
                // fara `width`/`height` explicit aici, iframe-ul iese cu dimensiunile
                // implicite 640x390px, indiferent de cutia parinte (`aspect-video`).
                width: '100%',
                height: '100%',
                // `youtube-nocookie.com`, obligatoriu: e un pas din inscrierea unui
                // profesor, intr-un produs pentru scoli.
                host: 'https://www.youtube-nocookie.com',
                playerVars: {
                    // Fara autoplay: pe mobil oricum nu porneste, iar un video care
                    // porneste singur peste un ecran de inscriere e agresiv.
                    autoplay: 0,
                    rel: 0,
                    modestbranding: 1,
                    playsinline: 1,
                    // Fara scurtaturile de tastatura ale playerului (sageti = +/-5 s, cifre =
                    // salt la 10 %...90 %). Inainte oricum n-ar trece de garda; asa nici nu
                    // pornesc, deci nu apare mesajul pentru o tasta apasata din greseala.
                    disablekb: 1
                },
                events: {
                    onReady: () => {
                        if (anulat) return;
                        setGata(true);
                        // Durata vine din API, niciodata scrisa de mana: un numar
                        // hardcodat ar minti in ziua in care se schimba materialul.
                        const d = playerRef.current?.getDuration?.() ?? 0;
                        if (Number.isFinite(d) && d > 0) setDurata(d);
                    },
                    onStateChange: (e: { data: number }) => {
                        ruleaza.current = e.data === STARE.RULEAZA;
                        if (e.data === STARE.RULEAZA) setPornit(true);
                        // La pauza si la salt lantul de esantioane se rupe: urmatorul
                        // esantion n-are voie sa se lege de unde ramasesem.
                        if (e.data !== STARE.RULEAZA) pozitieAnterioara.current = null;
                    }
                }
            });
        });

        return () => {
            anulat = true;
            playerRef.current?.destroy?.();
            playerRef.current = null;
        };
    }, [idVideo]);

    React.useEffect(() => {
        if (!pornit) return;
        const t = window.setInterval(() => {
            const p = playerRef.current;
            if (!p) return;
            const pozitie = p.getCurrentTime?.();
            if (!Number.isFinite(pozitie)) return;
            const d = p.getDuration?.();

            /* Cat a rulat playerul de la esantionul anterior, in secunde de FILM: timp real,
               doar daca rula atunci, inmultit cu viteza. In fundal browserul rareste timerul, dar
               filmul merge — fara asta, o vizionare cinstita cu fila ascunsa ar fi intoarsa. */
            const acum = performance.now();
            const rata = p.getPlaybackRate?.();
            const anterior = esantionAnterior.current;
            const secundeRedare = anterior?.ruleaza
                ? ((acum - anterior.la) / 1000) * (Number.isFinite(rata) && (rata as number) > 0 ? (rata as number) : 1)
                : 0;
            esantionAnterior.current = { la: acum, ruleaza: ruleaza.current };

            const verdict = gardaSaltInainte(
                maxVazut.current,
                pozitie as number,
                secundeRedare,
                Number.isFinite(d) ? (d as number) : 0
            );
            maxVazut.current = verdict.maxVazut;
            if (verdict.revinoLa !== null) {
                p.seekTo?.(verdict.revinoLa, true);
                // Saltul nu se leaga de urmatorul esantion: lantul de acoperire se rupe aici,
                // ca la orice salt (vezi `onStateChange`).
                pozitieAnterioara.current = null;
                setAvertismentSalt(true);
                if (ceasAvertisment.current !== null) window.clearTimeout(ceasAvertisment.current);
                ceasAvertisment.current = window.setTimeout(() => setAvertismentSalt(false), DURATA_AVERTISMENT_SALT_MS);
                return;
            }

            setSegmente((s) => adaugaEsantion(s, pozitieAnterioara.current, pozitie as number));
            pozitieAnterioara.current = pozitie as number;
            if (Number.isFinite(d) && (d as number) > 0) setDurata((v) => (v > 0 ? v : (d as number)));
        }, PAS_SONDAJ_MS);
        return () => window.clearInterval(t);
    }, [pornit]);

    /* Playerul care nu raporteaza NICIODATA durata: fara ceasul asta, ecranul
       ramane blocat pentru totdeauna si fara nicio explicatie. Se opreste singur
       in clipa in care durata soseste. */
    React.useEffect(() => {
        if (durata > 0 || eroare) return;
        const t = window.setTimeout(() => {
            setEroare('Filmulețul nu s-a încărcat. Poți încerca mai târziu — pasul acesta nu blochează cererea ta de înscriere.');
        }, PRAG_DURATA_MS);
        return () => window.clearTimeout(t);
    }, [durata, eroare]);

    const casute = casuteVizionate(segmente, durata, NR_CASUTE);
    const pragAtins = poateContinua(segmente, durata);
    const deschis = VIDEO_INTRO_SARIBIL || pragAtins;

    /* Marcajul pe cerere (SP-03) pleaca la PRAG, nu la clic: cine vede materialul si
       inchide fila a facut ce i s-a cerut. Santinela e in hook si se ridica doar la
       succes, ca un esec trecator sa se reincerce la urmatoarea vizita. */
    React.useEffect(() => {
        if (pragAtins) marcheaza();
    }, [pragAtins, marcheaza]);
    const vazute = secundeVazute(segmente);
    const ramase = secundeRamase(segmente, durata);
    const procent = Math.round(acoperire(segmente, durata) * 100);
    const aSarit = casute.some((c, i) => !c && casute.slice(i + 1).some(Boolean));

    const videoRaportat = React.useRef(false);
    React.useEffect(() => {
        if (!pragAtins || videoRaportat.current) return;
        videoRaportat.current = true;
        trackIntroVideoWatched(procent, aSarit);
    }, [pragAtins, procent, aSarit]);

    /* Antetul e in `CadruPasVideo`, comun cu starile `se-incarca` si `indisponibil`:
       cerinta lui S1 e ca titlul si caseta playerului sa nu se mute cu niciun pixel
       cand soseste raspunsul, si asta se tine prin acelasi DOM, nu prin doua copii
       care se nimeresc egale. */
    return (
        <CadruPasVideo
            pastila={durata > 0 ? formateazaDurataLunga(durata) : 'se încarcă…'}
            subsol={
                <>
                    {/* Butonul e ancorat jos din secunda zero: exista de la inceput, doar ca spune
                        cat mai e. `aria-disabled` in loc de `disabled`, ca sa NU cada pe
                        `disabled:opacity-50` din `buttonVariants` — o eticheta stearsa la 50%
                        coboara sub pragul de contrast exact cand omul are mai multa nevoie de ea. */}
                    <div className="fixed inset-x-0 bottom-0 z-10 border-t border-hairline bg-white px-4 pb-4 pt-3 safe-area-bottom">
                        <div className="mx-auto w-full max-w-2xl">
                            <Button
                                type="button"
                                aria-disabled={!deschis}
                                onClick={async () => {
                                    if (!deschis) return;
                                    marcheaza();
                                    // p8: nu navigam pe raspunsul local (`deschis`) singur -- asteptam
                                    // PATCH-ul sa se stabileasca, apoi reimprospatam tokenul, ca
                                    // resolverul sa vada claim-ul PROASPAT. Fara asta, poarta tare pe
                                    // video din resolver l-ar trimite inapoi aici la urmatoarea
                                    // navigare, cu un token vechi care inca zice "nevazut".
                                    await asteaptaMarcaj();
                                    const tokenNou = await handleTokenRefresh(true);
                                    const decodat = tokenNou ? jwtDecode<UserInfo>(tokenNou) : null;
                                    const tinta = decodat
                                        ? resolvePostAuthTarget(decodat, null, {
                                              PATHS,
                                              PAGES,
                                              ROLES,
                                              getUserHomePage,
                                              pasiInscriere: pasiInscriereActive(),
                                          })
                                        : null;
                                    // Fara `onContinua` injectat, tinta e ce decide resolverul --
                                    // cu protocolul activ: pasul de protocol pentru un cont CU scoala,
                                    // pagina de asteptare pentru unul fara. Protocolul nu se poate
                                    // descarca inainte de aprobare (409 din lipsa `schoolId`), deci
                                    // „continua" nu are voie sa promita un ecran care refuza.
                                    // Niciodata pagina de unde vine legatura, hardcodat aici.
                                    if (onContinua) onContinua();
                                    else navigate(tinta ?? caleAsteptare);
                                }}
                                className={'min-h-11 w-full ' + (deschis ? '' : 'cursor-not-allowed bg-light text-dark hover:bg-light')}
                            >
                                {deschis || durata === 0
                                    ? 'Continuă'
                                    : 'Continuă (mai sunt ' + formateazaDurata(ramase) + ')'}
                            </Button>
                            {/* Cu eroare afisata, promisiunea „se deblocheaza dupa ce vezi
                                filmuletul" ar fi falsa: filmuletul nu se poate vedea. Mesajul
                                si iesirea sunt deja in caseta de eroare. */}
                            {!deschis && !eroare && (
                                <p className="mt-2 text-center text-smaller text-gray-600 m-0">
                                    {aSarit
                                        ? 'Porțiunea sărită nu se bifează — o poți relua de unde e goală bara'
                                        : 'Se deblochează după ce vezi filmulețul'}
                                </p>
                            )}
                        </div>
                    </div>
                </>
            }
        >
            <div className="overflow-hidden rounded-lg border border-hairline bg-white">
                <div className="relative aspect-video w-full">
                    <div ref={containerRef} className="absolute inset-0 h-full w-full" />
                    {/* Mesajul garzii de salt, peste player — acolo e privirea in clipa in care
                        omul tocmai a tras de bara. Elementul exista MEREU (gol cand nu e nimic de
                        spus): o regiune `aria-live` montata odata cu textul ei nu se anunta sigur.
                        `pointer-events-none`: nu fura atingerile controalelor playerului. */}
                    <p
                        role="status"
                        aria-live="polite"
                        data-garda-salt
                        className={'pointer-events-none absolute inset-x-2 top-2 m-0 text-center text-small font-semibold text-white ' +
                            (avertismentSalt ? 'rounded-md bg-dark/80 px-3 py-2' : '')}
                    >
                        {avertismentSalt ? 'Poți reveni, dar nu poți sări înainte.' : ''}
                    </p>
                    {/* Start explicit: pe mobil autoplay-ul nu merge, iar un buton mare
                        e mai onest decat un player care pare stricat. */}
                    {gata && !pornit && (
                        <button
                            type="button"
                            onClick={() => playerRef.current?.playVideo?.()}
                            className="absolute inset-0 grid h-full w-full place-items-center bg-dark/60 text-white"
                        >
                            <span className="flex flex-col items-center gap-2">
                                <span className="grid size-16 place-items-center rounded-full bg-white">
                                    <Play className="size-7 text-magenta" aria-hidden />
                                </span>
                                <span className="text-fluid-base font-semibold">Pornește filmulețul</span>
                            </span>
                        </button>
                    )}
                </div>
            </div>

            {/* Caseta de eroare isi duce propria iesire: cand filmuletul nu se
                incarca, poarta ramane inchisa (acoperirea unei durate
                necunoscute e 0, deliberat), deci fara linkul asta ecranul e un
                fund de sac. */}
            {eroare && (
                <div data-eroare-video role="alert" className="mt-3 rounded-lg border border-error bg-error/10 p-3 text-small text-error">
                    <p className="m-0">{eroare}</p>
                    <Link
                        to={caleAsteptare}
                        className="mt-2.5 flex min-h-11 w-full items-center justify-center rounded-md border border-input bg-white px-4 font-semibold text-dark"
                    >
                        Înapoi la pagina de așteptare
                    </Link>
                </div>
            )}

            {/* Bara de SEGMENTE, nu de pozitie. */}
            <div
                className="mt-3 flex h-2 gap-0.5"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={procent}
                aria-label="Cât ați vizionat din filmuleț"
            >
                {casute.map((vazut, i) => (
                    <i key={i} className={'flex-1 rounded-sm ' + (vazut ? 'bg-magenta' : 'bg-muted')} />
                ))}
            </div>
            <div className="mt-1.5 flex justify-between gap-2.5 text-smaller text-gray-600">
                <span>
                    {pragAtins ? (
                        <>Ai văzut <strong className="text-dark">tot filmulețul</strong></>
                    ) : (
                        <>Ai văzut <strong className="text-dark tabular-nums">{formateazaDurata(vazute)}</strong></>
                    )}
                </span>
                <span className="tabular-nums">
                    {durata > 0
                        ? (pragAtins ? formateazaDurata(durata) + ' din ' : 'din ') + formateazaDurata(durata)
                        : ''}
                </span>
            </div>
        </CadruPasVideo>
    );
}
